import { DigitalAdoptionPlugin } from '@legacy/components/global/DigitalAdoptionPlugin';
import { StyledEngineProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfirmProvider } from 'material-ui-confirm';
import type { User } from 'oidc-client-ts';
import { usePostHog, PostHogProvider } from 'posthog-js/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router';

import { config, posthogOptionsConfig } from './config';
import { MainRouter } from './MainRouter';
import { isApiError } from '@/api/REST/errors';
import { USER_ME_KEY } from '@/api/REST/userMe';
import { identifyPostHogUser } from '@/auth/identifyPostHogUser';
import { TestLoginGate } from '@/auth/testLogin';
import { userManager } from '@/auth/userManager';
import { ErrorBoundary } from '@/components/global/ErrorBoundary';
import { LocalizationSetup } from '@/components/global/LocalizationSetup';
import { NotificationProvider } from '@/components/global/NotificationProvider';
import { PostHogUserProperties } from '@/components/global/PostHogUserProperties';
import { TimelogThemeProvider } from '@/components/global/TimelogThemeProvider.tsx';
import { useGlobalErrorHandler } from '@/hooks/useGlobalErrorHandler';
import { useHttpErrorHandler } from '@/hooks/useHttpErrorHandler';
import { useLocaleStore } from '@/stores/locale';

import '@/i18n/config';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Added as it was the default in old react app.
      // Might need to be revisited later.
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: true,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (isApiError(error) && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Don't retry globally-handled errors (403, 5xx) — toast already shown
        if ((error as { _handledByToast?: boolean })?._handledByToast) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
    },
  },
});

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

const AppContent = () => {
  const { handleGlobalError } = useGlobalErrorHandler();
  useHttpErrorHandler();

  return (
    <ErrorBoundary onError={handleGlobalError}>
      <BrowserRouter>
        <HelmetProvider>
          <Helmet htmlAttributes={{ lang: useLocaleStore.getState().lang }} />
          <TestLoginGate>
            <PostHogUserProperties />
            <MainRouter />
            <DigitalAdoptionPlugin />
          </TestLoginGate>
        </HelmetProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </ErrorBoundary>
  );
};

export const App = () => {
  const { lang: currentLang } = useLocaleStore();

  const posthog = usePostHog();

  const authSigninCallback = (user: User | void): void => {
    // removes auth payload from url
    window.history.replaceState({}, document.title, window.location.pathname);

    if (user) {
      identifyPostHogUser(posthog, user, currentLang);
      queryClient.invalidateQueries({
        queryKey: [USER_ME_KEY],
      });
    }
  };

  return (
    <AuthProvider
      onSigninCallback={authSigninCallback}
      userManager={userManager}
    >
      <PostHogProvider
        apiKey={config.posthogKey}
        options={posthogOptionsConfig}
      >
        <QueryClientProvider client={queryClient}>
          <StyledEngineProvider injectFirst>
            <TimelogThemeProvider>
              <ConfirmProvider>
                <NotificationProvider>
                  <LocalizationSetup>
                    <AppContent />
                  </LocalizationSetup>
                </NotificationProvider>
              </ConfirmProvider>
            </TimelogThemeProvider>
          </StyledEngineProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </AuthProvider>
  );
};
